import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useEffect } from 'react';
import { useTextHighlight } from '../utils/highlightUtils';

const Class18GuidedPractice02 = () => {
    const navigate = useNavigate();

    const highlightRef = useRef(null);
    useTextHighlight(highlightRef);

    const [showHelp, setShowHelp] = useState(false);

    const questionRefs = useRef([]);
    const rightColRef = useRef(null);

    // Make sure your answers state exists
    const [answers, setAnswers] = useState(Array(20).fill(''));
    const [currentQuestion, setCurrentQuestion] = useState(14);
    const onNext = () => {
        setCurrentQuestion(prev => Math.min(prev + 1, 19));
    };

    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);

    // Timer state
    const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
    const [showSeconds, setShowSeconds] = useState(false);

    // Add missing state for drag-and-drop headings
    const [draggedHeading, setDraggedHeading] = useState(null);
    const [selectedHeading, setSelectedHeading] = useState(null);
    const [headingAssignments, setHeadingAssignments] = useState({
        14: null,
        15: null,
        16: null,
        17: null,
        18: null,
        19: null,
        20: null
    });
    const headingList = [
        "A period in cold conditions before the technology is assessed",
        "Marketing issues lead to failure",
        "Good and bad aspects of steam technology are passed on",
        "A possible solution to the issues of today",
        "Further improvements lead to commercial orders",
        "Positive publicity at last for this quiet, clean, fast vehicle",
        "A disappointing outcome for customers",
        "A better option than the steam car arises"
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 0) {
                    clearInterval(timer);
                    navigate('/');  // Redirect to home
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [navigate]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (showSeconds) {
            return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
        }
        return `${mins} minutes remaining`;
    };

    const handleStartTest = () => {
        navigate(`/guided-practice-01-form`);
    };

    const handlePlayPause = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        setCurrentTime(audioRef.current.currentTime);
    };

    const handleLoadedMetadata = () => {
        if (!audioRef.current) return;
        setDuration(audioRef.current.duration);
    };

    const handleSeek = (e) => {
        if (!audioRef.current) return;
        const time = parseFloat(e.target.value);
        audioRef.current.currentTime = time;
        setCurrentTime(time);
    };

    const handleVolumeChange = (e) => {
        if (!audioRef.current) return;
        const vol = parseFloat(e.target.value);
        setVolume(vol);
        audioRef.current.volume = vol;
        setIsMuted(vol === 0);
    };

    const handleMute = () => {
        if (!audioRef.current) return;
        if (isMuted) {
            audioRef.current.muted = false;
            setIsMuted(false);
            audioRef.current.volume = volume;
        } else {
            audioRef.current.muted = true;
            setIsMuted(true);
        }
    };

    const GreenCheck = () => (
        <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="inline-block"
        >
            <path
                d="M7 11L9 13.5L16 7"
                stroke="#7DD77D"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );


    return (
        <div className="min-h-screen bg-white flex flex-col font-[Arial]">
            {/* Help Modal */}
            <HelpModal
                isOpen={showHelp}
                onClose={() => setShowHelp(false)}
            />
            {/* Top Bar (fixed) */}
            <div className="sticky top-0 z-30 bg-white">
                <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
                    {/* Left + Center combined on left side */}
                    <div className="flex flex-col justify-center space-y-0.5 pl-6">
                        <span className="text-[14px] text-left text-black font-bold leading-tight">
                            HEXA'S Education
                        </span>
                        <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
                            <span
                                onMouseEnter={() => setShowSeconds(true)}
                                onMouseLeave={() => setShowSeconds(false)}
                                className="cursor-pointer"
                            >
                                {formatTime(timeRemaining)}
                            </span>
                        </div>
                    </div>
                    {/* Right: Icons and Finish Button */}
                    <div className="flex items-center space-x-5">
                        <button
                            onClick={() => navigate('/')}
                            className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
                        >
                            Back Home
                        </button>
                        <button
                            onClick={() => setShowHelp(true)}
                            className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
                        >
                            Finish test
                        </button>
                        <div className="flex items-center space-x-8 pl-2 pr-2">
                            {/* WiFi Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 717.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            {/* Notification Bell Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                </svg>
                            </div>
                            {/* Hamburger Menu Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            {/* Pen/Edit Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Title Bar (fixed under top bar) */}
                <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
                    {/* Left: Part Title */}
                    <div className="flex-1 px-4 py-2 text-left">
                        <h2 className="text-[23px] text-black">Part 1</h2>
                        <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
                    </div>
                    {/* Center: Absolutely Positioned Logo */}
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <img
                            src={HEXASLogo}
                            alt="HEXA'S Logo"
                            className="h-[50px] object-contain"
                        />
                    </div>
                    {/* Right: Empty space for consistency */}
                    <div className="flex items-center justify-end w-[300px] pr-4">
                        {/* Empty space - no audio player for reading */}
                    </div>
                </div>
            </div>
            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                <div ref={highlightRef}
                    className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
                    style={{ height: 'calc(100vh - 180px)' }}
                >
                    {/* Left: Reading Passage */}
                    <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
                        <div className="mb-8">
                            <h2 className="text-[16px] font-bold mb-4 text-center">THE STEAM CAR</h2>


                            <div className="text-[14px] space-y-4 text-left">

                                {/* Section A */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[0] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[14] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[14] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 14: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 14: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[14] !== null
                                            ? headingList[headingAssignments[14]]
                                            : '14'}
                                    </span>
                                    <br />
                                    <p>
                                        When primitive automobiles first began to appear in the 1800s, their engines were based on steam power. Steam had already enjoyed a long and successful career in the railways, so it was only natural that the technology evolved into a miniaturized version which was separate from the trains. But these early cars inherited steam’s weaknesses along with its strengths. The boilers had to be lit by hand, and they required about twenty minutes to build up pressure before they could be driven. Furthermore, their water reservoirs only lasted for about thirty miles before needing replenishment. Despite such shortcomings, these newly designed self-propelled carriages offered quick transportation, and by the early 1900s it was not uncommon to see such machines shuttling wealthy citizens around town.
                                    </p>
                                </div>

                                {/* Section B */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[1] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[15] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[15] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 15: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 15: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[15] !== null
                                            ? headingList[headingAssignments[15]]
                                            : '15'}
                                    </span>
                                    <br />
                                    <p>
                                        But the glory days of steam cars were few. A new technology called the Internal Combustion Engine soon appeared, which offered the ability to drive down the road just moments after starting up. At first, these noisy gasoline cars were unpopular because they were more complicated to operate and they had difficult hand-crank starters, which were known to break arms when the engines backfired. But in 1912 General Motors introduced the electric starter, and over the following few years steam power was gradually phased out.
                                    </p>
                                </div>

                                {/* Section C */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[2] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[16] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[16] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 16: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 16: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[16] !== null
                                            ? headingList[headingAssignments[16]]
                                            : '16'}
                                    </span>
                                    <br />
                                    <p>
                                        Even as the market was declining, four brothers made one last effort to rekindle the technology. Between 1906 and 1909, while still attending high school, Abner Doble and his three brothers built their first steam car in their parents’ basement. It comprised parts taken from a wrecked early steam car but reconfigured to drive an engine of their own design. Though it did not run well, the Doble brothers went on to build a second and third prototype in the following years. Though the Doble boys’ third prototype, nicknamed the Model B, still lacked the convenience of an internal combustion engine, it drew the attention of automobile trade magazines due to its numerous improvements over previous steam cars. The Model B proved to be superior to gasoline automobiles in many ways. Its high-pressure steam drove the engine pistons in virtual silence, in contrast to clattering gas engines which emitted the aroma of burned hydrocarbons. Perhaps most impressively, the Model B was amazingly swift. It could accelerate from zero to sixty miles per hour in just fifteen seconds, a feat described as ‘remarkable acceleration’ by Automobile magazine in 1914.
                                    </p>
                                </div>

                                {/* Section D */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[3] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[17] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[17] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 17: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 17: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[17] !== null
                                            ? headingList[headingAssignments[17]]
                                            : '17'}
                                    </span>
                                    <br />
                                    <p>
                                        The following year Abner Doble drove the Model B from Massachusetts to Detroit in order to seek investment in his automobile design, which he used to open the General Engineering Company. He and his brothers immediately began working on the Model C, which was intended to expand upon the innovations of the Model B. The brothers added features such as a key-based ignition in the cabin, eliminating the need for the operator to manually ignite the boiler. With these enhancements, the Dobles’ new car company promised a steam vehicle which would provide all of the convenience of a gasoline car, but with much greater speed, much simpler driving controls, and a virtually silent powerplant. By the following April, the General Engineering Company had received 5,390 deposits for Doble Detroits, which were scheduled for delivery in early 1918.
                                    </p>
                                </div>

                                {/* Section E */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[4] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[18] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[18] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 18: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 18: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[18] !== null
                                            ? headingList[headingAssignments[18]]
                                            : '18'}
                                    </span>
                                    <br />
                                    <p>
                                        Later that year Abner Doble delivered unhappy news to those eagerly awaiting the delivery of their modern new cars. Those buyers who received the handful of completed cars complained that the vehicles were sluggish and erratic, sometimes going in reverse when they should go forward. The new engine design, though innovative, was still plagued with serious glitches.
                                    </p>
                                </div>

                                {/* Section F */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[5] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[19] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[19] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 19: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 19: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[19] !== null
                                            ? headingList[headingAssignments[19]]
                                            : '19'}
                                    </span>
                                    <br />
                                    <p>
                                        The brothers made one final attempt to produce a viable steam automobile. In early 1924, the Doble brothers shipped a Model E to New York City to be road-tested by the Automobile Club of America. After sitting overnight in freezing temperatures, the car was pushed out into the road and left to sit for over an hour in the frosty morning air. At the turn of the key, the boiler lit and reached its operating pressure inside of forty seconds. As they drove the test vehicle further, they found that its evenly distributed weight lent it surprisingly good handling, even though it was so heavy. As the new Doble steamer was further developed and tested, its maximum speed was pushed to over a hundred miles per hour, and it achieved about fifteen miles per gallon of kerosene with negligible emissions.
                                    </p>
                                </div>

                                {/* Section G */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[6] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[20] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[20] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 20: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 20: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[20] !== null
                                            ? headingList[headingAssignments[20]]
                                            : '20'}
                                    </span>
                                    <br />
                                    <p>
                                        Sadly, the Dobles’ brilliant steam car never was a financial success. Priced at around $18,000 in 1924, it was popular only among the very wealthy. Plus, it is said that no two Model Es were quite the same, because Abner Doble tinkered endlessly with the design. By the time the company folded in 1931, fewer than fifty of the amazing Model E steam cars had been produced. For his whole career, until his death in 1961, Abner Doble remained adamant that steam-powered automobiles were at least equal to gasoline cars, if not superior. Given the evidence, he may have been right. Many of the Model E Dobles which have survived are still in good working condition, some having been driven over half a million miles with only normal maintenance. Astonishingly, an unmodified Doble Model E runs clean enough to pass the emissions laws in California today, and they are pretty strict. It is true that the technology poses some difficult problems, but you cannot help but wonder how efficient a steam car might be with the benefit of modern materials and computers. Under the current pressure to improve automotive performance and reduce emissions, it is not unthinkable that the steam car may rise again.
                                    </p>
                                </div>

                            </div>
                        </div>
                    </div>






                    {/* Right: List of Headings */}
                    <div
                        ref={rightColRef}
                        className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto"
                    >
                        <div className="mb-10 w-full">
                            <h3 className="text-[15px] font-bold mb-4 text-left">Questions 14 – 20</h3>
                            <p className="text-[14px] mb-6 text-left">
                                The text has seven sections. Choose the correct heading for each section from the list below. Drag and drop the headings into the correct boxes on the left.
                            </p>

                            <div className="mb-6">
                                <p className="text-[16px] font-bold mb-4">List of Headings</p>
                                <div className="flex flex-col gap-2">
                                    {[
                                        "A period in cold conditions before the technology is assessed",
                                        "Marketing issues lead to failure",
                                        "Good and bad aspects of steam technology are passed on",
                                        "A possible solution to the issues of today",
                                        "Further improvements lead to commercial orders",
                                        "Positive publicity at last for this quiet, clean, fast vehicle",
                                        "A disappointing outcome for customers",
                                        "A better option than the steam car arises"
                                    ].map((heading, idx) => {
                                        const assigned = Object.values(headingAssignments).includes(idx);
                                        if (assigned) return null; // Only show unassigned headings
                                        const isSelected = selectedHeading === idx;

                                        return (
                                            <div
                                                key={heading}
                                                className={`font-bold rounded-lg shadow-sm px-4 py-1 text-[14px] text-black select-none border border-[#cfd9e4] cursor-pointer transition-all
                    ${isSelected ? 'bg-blue-100 border-blue-400' : 'bg-transparent hover:bg-[#e6f0fa]/40'}`}
                                                draggable
                                                onDragStart={e => {
                                                    setDraggedHeading(idx);
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    e.dataTransfer.setData('headingIdx', String(idx));
                                                }}
                                                onDragEnd={() => setDraggedHeading(null)}
                                                onClick={() => setSelectedHeading(idx)}
                                            >
                                                {heading}
                                            </div>
                                        );
                                    })}
                                </div>


                            </div>
                        </div>
                    </div>









                </div>
            </div >
            {/* Bottom Navigation (fixed footer) */}
            < div className="sticky bottom-0 left-0 w-full bg-[#D7DFEC] border-t border-gray-300 z-30 flex items-center justify-between px-4 py-3" >
                {/* Left: Question Buttons */}
                < div className="flex items-center space-x-1" >
                    {
                        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((questionNumber) => (
                            <button
                                key={questionNumber}
                                onClick={() => setCurrentQuestion(questionNumber)}
                                className={`w-7 h-7 mx-0.5 rounded text-[12px] font-bold flex items-center justify-center ${currentQuestion === questionNumber
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-black text-white'
                                    }`}
                            >
                                {questionNumber}
                            </button>
                        ))
                    }
                </div >

                {/* Right: Arrows */}
                < div className="flex items-center space-x-2" >
                    {/* Backward Arrow */}
                    < button
                        onClick={() => {
                            setCurrentQuestion((prev) => Math.max(1, prev - 1));
                        }}
                        className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 1 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
                            } text-white`}
                        title="Previous Question"
                        disabled={currentQuestion === 1}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className="w-4 h-4"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button >

                    {/* Forward Arrow */}
                    < button
                        onClick={() => {
                            setCurrentQuestion((prev) => Math.min(10, prev + 1));
                        }}
                        className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 10 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
                            } text-white`}
                        title="Next Question"
                        disabled={currentQuestion === 10}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className="w-4 h-4"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button >
                </div >
            </div >
        </div >
    );
};

export default Class18GuidedPractice02;
